import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FieldError, svgSprite, Tooltip } from '../../../../../hybrid/wrappers';
import Title from '../../Title';
import ConfirmModal from '../../../../../widgets/deleteModal/DeleteModal';
import { t } from '../../../../../common/components/T';
import { LangContext } from '../../../../../common/decorators/Language';
import css from './FormCheckbox.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../../../common/utils/styles';

const FormCheckbox = ({
    attribute,
    hint,
    disabled,
    title,
    handlers = {},
    compStyle,
    value,
    setFormData = () => {},
    errors,
    reload,
    confirm
}) => {
    const { stylers, classes } = generateCompStylers(compStyle);
    const [val, setVal] = useState(value || '0');
    const [showConfirm, setShowConfirm] = useState(false);
    const { translation } = useContext(LangContext) || {};
    const changeHandler = useCallback(() => {
        if (!disabled) {
            setFormData(attribute, val === '0' ? '1' : '0');
            setVal(val === '0' ? '1' : '0');
            if (handlers?.onChange) {
                handlers.onChange(attribute, val === '0' ? '1' : '0');
            }
        }
        setShowConfirm(false);
    }, [disabled, setFormData, attribute, val, handlers]);
    useEffect(() => {
        setVal(value);
    }, [value]);
    useEffect(() => {
        setVal(value || '0');
    }, [reload, value]);
    return (
        <div
            style={stylers.wrapper}
            className={classnames(css.formCheckboxWrapper, classes.wrapper, {
                [classes.wrapperActive]: val === '1' || val === true
            })}
        >
            <div
                onClick={
                    val === '0' && confirm && !disabled
                        ? () => {
                              setShowConfirm(true);
                          }
                        : changeHandler
                }
                style={
                    val === '1' || val === true
                        ? {
                              ...stylers.label,
                              ...stylers.labelActive
                          }
                        : stylers.label
                }
                className={classnames(
                    css.formCheckboxBlock,
                    disabled ? css.formCheckboxBlockDisabled : {},
                    val === '1' || val === true ? [classes.labelActive, classes.label] : classes.label
                )}
            >
                <div
                    style={
                        val === '1' || val === true
                            ? {
                                  ...stylers.checkBox,
                                  ...stylers.formCheckboxActive
                              }
                            : stylers.checkBox
                    }
                    className={classnames(
                        css.formCheckbox,
                        classes.checkBox,
                        val === '1' || val === true ? [css.formCheckboxActive, classes.formCheckboxActive] : null
                    )}
                >
                    {val === '1' || val === true
                        ? svgSprite('check', {
                              fill: 'var(--whiteColor)',
                              className: classnames(css.formCheckboxSvg, classes.checkboxSvg),
                              style: stylers.checkboxSvg
                          })
                        : null}
                </div>
                {!!title && <Title Comp={'p'} body={title.body} links={title.links} />}
                {hint && (
                    <Tooltip
                        text={hint}
                        compStyle={{
                            inner: classnames(title.hintClassName, css.formInfoTooltip),
                            tooltipWrapCustom: classes?.tooltipWrapCustom
                        }}
                    >
                        {svgSprite('info-circle', {
                            className: css.formInfoIcon
                        })}
                    </Tooltip>
                )}
            </div>
            {errors && errors.length
                ? errors.map((err, idx) => (
                      <FieldError compStyle={compStyle?.error} key={`${attribute}${idx}`}>
                          {err}
                      </FieldError>
                  ))
                : null}
            {confirm && (
                <ConfirmModal
                    customStyles={{
                        singleButton: { flexGrow: 1 },
                        modalWrapper: { width: '576px' },
                        modalBodyText: { maxWidth: 'none' }
                    }}
                    showModal={showConfirm}
                    setShowModal={setShowConfirm}
                    typeModal={false}
                    title={t('confirmAction', 'Подтвердите действие', 'all', translation)}
                    text={confirm}
                    modalFooterShow
                    buttons={[
                        {
                            type: 'cancel',
                            buttonText: t('cancel', 'Отмена', 'all', translation),
                            clickFunc: () => setShowConfirm(false)
                        },
                        {
                            type: 'ok',
                            buttonText: t('confirm', 'Подтвердить', 'all', translation),
                            clickFunc: changeHandler
                        }
                    ]}
                />
            )}
        </div>
    );
};
FormCheckbox.propTypes = {
    attribute: PropTypes.string,
    hint: PropTypes.node,
    disabled: PropTypes.bool,
    title: PropTypes.object,
    handlers: PropTypes.object,
    compStyle: PropTypes.object,
    value: PropTypes.string,
    setFormData: PropTypes.func,
    errors: PropTypes.array,
    reload: PropTypes.bool,
    confirm: PropTypes.node
};
export default FormCheckbox;
